import { type MutableRefObject, useLayoutEffect, useState } from 'react'

/**
 * Ruler alignment inputs measured from layout: the spread's origin in scroll-content coordinates
 * and the pasteboard's scroll extents. Re-measured when layout changes, never per scroll.
 */
export function useRulerContentOrigin({
  scrollRef,
  documentCanvasRef,
  pasteboardRef,
  scale,
  pasteboardSize,
  zoom,
  bleedWidth,
  bleedHeight,
  trimWidth,
  trimHeight,
  bleedMargin,
  liveInset,
  centerGap,
  checkpointLoadKey,
}: {
  scrollRef: MutableRefObject<HTMLDivElement | null>
  documentCanvasRef: MutableRefObject<HTMLDivElement | null>
  pasteboardRef: MutableRefObject<HTMLDivElement | null>
  scale: number
  /** On-screen pasteboard size (`pasteboardSize` in `use-spread-layout`). */
  pasteboardSize: number
  zoom: number
  bleedWidth: number
  bleedHeight: number
  trimWidth: number
  trimHeight: number
  bleedMargin: number
  liveInset: number
  centerGap: number
  checkpointLoadKey?: string
}) {
  /** Scroll-content (x,y) of spread container; snapshotted on layout changes only — stable while panning. */
  const [rulerContentOrigin, setRulerContentOrigin] = useState<{
    x: number
    y: number
  } | null>(null)
  /**
   * Measured pasteboard scrollWidth/Height (can exceed `pasteboardSize` when scaled content
   * overflows).
   */
  const [pasteboardScrollExtents, setPasteboardScrollExtents] = useState({
    width: pasteboardSize,
    height: pasteboardSize,
  })

  // Ruler alignment: snapshot origin when layout changes, not on every scroll — avoids subpixel
  // getBoundingClientRect drift while panning (tick.pos must use a fixed content-space origin).
  useLayoutEffect(() => {
    const scroller = scrollRef.current
    const canvasEl = documentCanvasRef.current

    if (!scroller || !canvasEl) return

    const snapshot = () => {
      const scRect = scroller.getBoundingClientRect()
      const canvasRect = canvasEl.getBoundingClientRect()

      setRulerContentOrigin({
        x: scroller.scrollLeft + (canvasRect.left - scRect.left),
        y: scroller.scrollTop + (canvasRect.top - scRect.top),
      })

      const board = pasteboardRef.current
      const w = Math.max(scroller.scrollWidth, board?.scrollWidth ?? 0, pasteboardSize)
      const h = Math.max(scroller.scrollHeight, board?.scrollHeight ?? 0, pasteboardSize)

      setPasteboardScrollExtents({ width: w, height: h })
    }

    snapshot()
    requestAnimationFrame(() => snapshot())

    const ro = new ResizeObserver(() => snapshot())

    ro.observe(scroller)

    const boardEl = pasteboardRef.current

    if (boardEl) ro.observe(boardEl)

    return () => ro.disconnect()
  }, [
    scale,
    pasteboardSize,
    zoom,
    bleedWidth,
    bleedHeight,
    trimWidth,
    trimHeight,
    bleedMargin,
    liveInset,
    centerGap,
    checkpointLoadKey,
    scrollRef,
    documentCanvasRef,
    pasteboardRef,
  ])

  return { rulerContentOrigin, pasteboardScrollExtents }
}
